import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import type { AvailabilityResponse, CategoryAvailability, RatePlanOption } from 'api/types/availability.types';
import type { BookingDetailLineRequest } from 'api/types/booking.types';
import type { MealPlan } from 'api/types/mealPlan.types';
import { mealPlanService } from 'api/services/mealPlanService';
import { calcLineByModel, classifyChildrenByAge, formatCurrency, getAvailBadgeClass } from '../booking.utils';
import CommercialLinesSection, { emptyCommercialLine } from './CommercialLinesSection';
import type { CommercialLineRow } from './CommercialLinesSection';
import AvailabilityCalendarPanel from './AvailabilityCalendarPanel';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Component-wise rate adjustment fields (D3 — see
 * crs-doc/CRS-Booking-Rate-Adjustment-Design.md). The operator types the
 * AGREED unit rate; the rack rate on the line stays untouched and the delta is
 * derived. Undefined = no adjustment on that component.
 *
 * These live on the UI line only — they are NOT part of the detail-line
 * payload; BookingFormModal turns them into the booking's `adjustments[]`.
 */
export interface RateAdjustmentFields {
  /** Agreed room tariff per room-night. */
  agreedRatePerRoom?: number;
  /** Agreed meal rate per adult-night. */
  agreedMealAdultRate?: number;
  /** Agreed extra-person rate per person-night. */
  agreedExtraAdultRate?: number;
  /** Negotiation reason — mandatory once any component is a discount. */
  adjustmentRemarks?: string;
}

export interface DetailLineUI extends BookingDetailLineRequest, RateAdjustmentFields {
  /**
   * Stable client-side identity for this line. Needed because a category can now
   * carry SEVERAL lines (double sharing, triple sharing…), so roomCategoryId no
   * longer identifies one. Never sent to the server — the payload builder drops it.
   */
  lineKey: string;
  categoryName: string;
  categoryCode: string;
  /** Display name of the allocated (upgrade) category when allocatedRoomCategoryId is set. */
  allocatedCategoryName?: string;
  /**
   * How many of numberOfRooms are hosted in the allocated category (E1 partial
   * upgrade). 0/undefined = no upgrade; equal to numberOfRooms = full upgrade;
   * in between = partial — the payload builder splits this card into two request
   * lines (base + upgraded) with the SAME booked category and rate.
   */
  upgradeRooms?: number;
  /**
   * The desk has chosen to carry this line's shortage rather than upgrade it
   * (booking-side oversell). UI-only — the payload builder never forwards it;
   * it drives `acceptOversell` on the request and relaxes the "upgrade at least
   * N rooms" rule that otherwise makes a sold-out category unbookable.
   */
  acceptShortage?: boolean;
  ratePlanName: string;
  mealPlanCode: string;
  mealPlanName: string;
  baseOccupancy: number;
  childAgeLimit?: number;
  chargeableChildAge?: number;
  infantAgeLimit?: number;
  includeTaxes?: boolean;
}

interface RoomRateSectionProps {
  availabilityResult: AvailabilityResponse | null;
  detailLines: DetailLineUI[];
  onDetailLinesChange: (lines: DetailLineUI[]) => void;
  numNights: number;
  fieldErrors: Record<string, string>;
  checkingAvailability: boolean;
  propertyId: string;
  checkInDate: string;
  checkOutDate: string;
  childAges?: (number | undefined)[];
  /** Property-level pricing model. Drives rate-pill headline and inline breakdown. */
  pricingModel?: string;
  /**
   * Whether the user may accept a booking beyond available inventory. Decides
   * only what the cards OFFER — the server re-checks the permission, the
   * property policy, the cap and the cutoff on every save.
   */
  canOversell?: boolean;
  /**
   * Per-pax commercial state, owned by the modal (it builds the payload).
   * When the basis is PER_PAX the cards are replaced by commercial lines +
   * an allocation list — the model the walk-in rewrite established, and the
   * one the backend prices from. The per-pax fields ON the cards are the
   * superseded first draft and are unreachable once this branch renders.
   */
  commercialLines: CommercialLineRow[];
  onCommercialLinesChange: (lines: CommercialLineRow[]) => void;
  totalRooms: string;
  onTotalRoomsChange: (v: string) => void;
}

// ============================================================================
// STEPPER CONTROL
// ============================================================================

interface StepperControlProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  /** Renders the value as a typed number input — needed for group bookings where clicking +30 times is unreasonable. */
  editable?: boolean;
}

const StepperControl: React.FC<StepperControlProps> = ({ label, value, min, max, onChange, disabled, editable }) => {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  return (
    <div className="stepper-control">
      <div className="stepper-label">{label}</div>
      <div className="stepper-group">
        <button
          type="button"
          className="stepper-btn"
          onClick={() => onChange(value - 1)}
          disabled={disabled || value <= min}
        >
          <IconSystem.minus size={12} />
        </button>
        {editable ? (
          <input
            type="number"
            className="stepper-value"
            style={{ width: '3rem', textAlign: 'center', border: 'none', background: 'transparent', color: 'inherit', font: 'inherit' }}
            value={value}
            min={min}
            max={max}
            disabled={disabled}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              if (!Number.isNaN(parsed)) onChange(clamp(parsed));
            }}
            onBlur={(e) => {
              const parsed = parseInt(e.target.value, 10);
              onChange(Number.isNaN(parsed) ? min : clamp(parsed));
            }}
          />
        ) : (
          <div className="stepper-value">{value}</div>
        )}
        <button
          type="button"
          className="stepper-btn"
          onClick={() => onChange(value + 1)}
          disabled={disabled || value >= max}
        >
          <IconSystem.add size={12} />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// GROUPED RATE PLAN — one entry per unique rate plan with meal plan details
// ============================================================================

interface GroupedRatePlan {
  ratePlanId: string;
  rateName: string;
  rateType?: string;
  ratePerRoom: number;
  extraAdultRate: number;
  extraChildRate: number;
  childAgeLimit?: number;
  chargeableChildAge?: number;
  infantAgeLimit?: number;
  includeTaxes?: boolean;
  minNights?: number;
  maxNights?: number;
  details: RatePlanOption[];       // all meal plan options for this rate plan
  defaultDetail: RatePlanOption;   // detail with isDefaultMealPlan = true (or first)
}

/** Group flat RatePlanOption[] by ratePlanId into one entry per rate plan */
const groupRatePlans = (ratePlans: RatePlanOption[]): GroupedRatePlan[] => {
  const map = new Map<string, GroupedRatePlan>();
  for (const rp of ratePlans) {
    let group = map.get(rp.ratePlanId);
    if (!group) {
      group = {
        ratePlanId: rp.ratePlanId,
        rateName: rp.rateName,
        rateType: rp.rateType,
        ratePerRoom: rp.ratePerRoom,
        extraAdultRate: rp.extraAdultRate,
        extraChildRate: rp.extraChildRate,
        childAgeLimit: rp.childAgeLimit,
        chargeableChildAge: rp.chargeableChildAge,
        infantAgeLimit: rp.infantAgeLimit,
        includeTaxes: rp.includeTaxes,
        minNights: rp.minNights,
        maxNights: rp.maxNights,
        details: [],
        defaultDetail: rp,
      };
      map.set(rp.ratePlanId, group);
    }
    group.details.push(rp);
    if (rp.isDefaultMealPlan) group.defaultDetail = rp;
  }
  return Array.from(map.values());
};

// ============================================================================
// ROOM CATEGORY CARD (full — after availability check with rate plans)
// ============================================================================

interface RoomCategoryCardProps {
  category: CategoryAvailability;
  line: DetailLineUI | undefined;
  numNights: number;
  onAdd: (cat: CategoryAvailability, ratePlan: RatePlanOption) => void;
  onUpdate: (lineKey: string, updates: Partial<DetailLineUI>) => void;
  onRemove: (lineKey: string) => void;
  /** Second and later occupancy lines of the same category — header is suppressed. */
  compact?: boolean;
  /** 0-based position of this line within its category, for the occupancy label. */
  occupancyIndex?: number;
  hasError: boolean;
  /**
   * The `line-<categoryId>` validation message, when there is one. Rendered in
   * the card header: `hasError` alone only tinted the border, so a save could be
   * refused with "Please fix the highlighted errors" and nothing on screen
   * saying what was wrong or where.
   */
  errorMessage?: string;
  childAges?: (number | undefined)[];
  allMealPlans: MealPlan[];
  pricingModel?: string;
  /** All categories in the availability result — source for the upgrade-allocation options (E1). */
  allCategories: CategoryAvailability[];
  /** May the user carry a shortage on this line instead of upgrading it? */
  canOversell?: boolean;
}

const RoomCategoryCard: React.FC<RoomCategoryCardProps> = ({
  category, line, numNights, onAdd, onUpdate, onRemove, hasError, errorMessage, childAges, allMealPlans, pricingModel,
  allCategories, canOversell = false, compact = false, occupancyIndex = 0,
}) => {
  const isPerPax = line?.rateBasis === 'PER_PAX';
  // Prefer the line's snapshot model (edit/view) over the property's current config.
  // This keeps the inline breakdown consistent with the stored totals even if the
  // property's pricing_model was flipped after the booking was created.
  const effectiveModel = line?.pricingModel || pricingModel;
  const isBundled = effectiveModel?.trim().toLowerCase() === 'bundled';
  const [previewExpanded, setPreviewExpanded] = useState(false);
  /** Rate-adjustment editor visibility for this category card. */
  const [adjustOpen, setAdjustOpen] = useState(false);

  /**
   * Key used when this card mutates its line. Empty only before a line exists,
   * in which case every mutating path goes through onAdd instead.
   */
  const lineKeyOrNew = line?.lineKey ?? '';

  const isSoldOut = category.availableRooms === 0;
  const isLowAvail = category.availableRooms > 0 && category.availableRooms <= 2;
  const isExpanded = !!line || previewExpanded;

  // ── Upgrade allocation (E1, partial-upgrade capable) ─────────────────────
  // The agent enters the FULL requested quantity for this (booked) category.
  // Rooms beyond the category's availability — or any voluntarily chosen count —
  // are hosted in the allocated (upgrade) category at THIS category's rate.
  // upgradeRooms < numberOfRooms produces a split: two payload lines with the
  // same booked category (base + upgraded), built in BookingFormModal.
  const upgradeRooms = Math.min(line?.upgradeRooms ?? 0, line?.numberOfRooms ?? 0);
  const baseRooms = (line?.numberOfRooms ?? 0) - upgradeRooms;
  /** Rooms requested beyond this category's own availability — must be upgraded. */
  const shortage = line ? Math.max(0, line.numberOfRooms - category.availableRooms) : 0;
  const allocatedCategory = useMemo(
    () => (line?.allocatedRoomCategoryId
      ? allCategories.find(c => c.roomCategoryId === line.allocatedRoomCategoryId) ?? null
      : null),
    [line?.allocatedRoomCategoryId, allCategories],
  );
  const upgradeOptions: SelectOption[] = useMemo(() => [
    ...allCategories
      .filter(c => c.roomCategoryId !== category.roomCategoryId && c.availableRooms > 0)
      .map(c => ({
        value: c.roomCategoryId,
        label: `${c.categoryName} (${c.availableRooms} available)`,
      })),
  ], [allCategories, category.roomCategoryId]);
  const handleUpgradeTargetChange = useCallback((value: string | string[] | null) => {
    const allocatedId = (value as string) || '';
    const target = allocatedId ? allCategories.find(c => c.roomCategoryId === allocatedId) : undefined;
    onUpdate(lineKeyOrNew, {
      allocatedRoomCategoryId: allocatedId || undefined,
      allocatedCategoryName: target?.categoryName,
    });
  }, [allCategories, lineKeyOrNew, onUpdate]);
  const handleUpgradeCountChange = useCallback((newCount: number) => {
    // Never below the shortage (those rooms have nowhere else to go) and never
    // above the requested quantity. Zero clears the allocation entirely.
    const floor = line?.acceptShortage ? 0 : shortage;
    const clamped = Math.max(floor, Math.min(newCount, line?.numberOfRooms ?? 0));
    onUpdate(lineKeyOrNew, clamped <= 0
      ? { upgradeRooms: undefined, allocatedRoomCategoryId: undefined, allocatedCategoryName: undefined }
      : { upgradeRooms: clamped });
  }, [lineKeyOrNew, line?.numberOfRooms, line?.acceptShortage, onUpdate, shortage]);

  // Inline upgrade validation — surfaced on the card; BookingFormModal blocks
  // save on the same conditions.
  const upgradeError = !line ? ''
    : shortage > 0 && upgradeRooms < shortage && !line.acceptShortage
      ? `${category.categoryName} has only ${category.availableRooms} available — at least ${shortage} room${shortage !== 1 ? 's' : ''} must be upgraded.`
    : upgradeRooms > 0 && !line.allocatedRoomCategoryId
      ? 'Choose the category to host the upgraded rooms.'
    : upgradeRooms > 0 && allocatedCategory && upgradeRooms > allocatedCategory.availableRooms
      ? `${allocatedCategory.categoryName} has only ${allocatedCategory.availableRooms} room${allocatedCategory.availableRooms !== 1 ? 's' : ''} available.`
    : '';

  // Group rate plans — one pill per unique rate plan
  const grouped = useMemo(() => groupRatePlans(category.ratePlans || []), [category.ratePlans]);
  const uniqueRatePlanCount = grouped.length;

  // Best rate plan (lowest ratePerRoom among groups)
  const bestRatePlanId = useMemo(() => {
    if (!grouped.length) return null;
    return grouped.reduce((b, g) => g.ratePerRoom < b.ratePerRoom ? g : b).ratePlanId;
  }, [grouped]);

  // The currently selected group (matches line's ratePlanId)
  const selectedGroup = useMemo(() => {
    if (!line) return null;
    return grouped.find(g => g.ratePlanId === line.ratePlanId) || null;
  }, [line, grouped]);

  // A sold-out category is still selectable when another category has space —
  // the guest gets upgraded (allocated) there at this category's rate (E1) —
  // or when the user may carry the shortage as an oversell.
  //
  // upgradeOptions holds ONLY other categories with space, so a single available
  // target is length 1. The old `> 1` test reported "no upgrade target" whenever
  // exactly one category could host the guest, which silently removed the only
  // action on the card.
  const hasUpgradeTargets = upgradeOptions.length > 0;
  const canSelect = !isSoldOut || hasUpgradeTargets || canOversell;

  const handleHeaderClick = useCallback(() => {
    if (!canSelect) return;
    if (!line) setPreviewExpanded(prev => !prev);
  }, [line, canSelect]);

  // Quick-add uses best rate plan's default meal plan detail
  const handleQuickAdd = useCallback(() => {
    if (!canSelect || !grouped.length) return;
    const bestGroup = grouped.find(g => g.ratePlanId === bestRatePlanId) || grouped[0];
    onAdd(category, bestGroup.defaultDetail);
    setPreviewExpanded(false);
  }, [canSelect, grouped, bestRatePlanId, category, onAdd]);

  // Select a rate plan group — uses its default meal plan detail. Snapshot the
  // default meal rates onto the line; bundled pricing uses them to compute the
  // upgrade diff when the user switches to a non-default meal plan.
  const handleRatePlanSelect = useCallback((group: GroupedRatePlan) => {
    const detail = group.defaultDetail;
    if (!line) {
      onAdd(category, detail);
      setPreviewExpanded(false);
    } else {
      onUpdate(lineKeyOrNew, {
        ratePlanId: detail.ratePlanId,
        ratePlanDetailId: detail.ratePlanDetailId,
        ratePlanName: detail.rateName,
        mealPlanId: detail.mealPlanId || undefined,
        mealPlanCode: detail.mealPlanCode || '',
        mealPlanName: detail.mealPlanName || '',
        ratePerRoom: detail.ratePerRoom,
        extraAdultRate: detail.extraAdultRate,
        extraChildRate: detail.extraChildRate,
        mealAdultRate: detail.mealAdultRate,
        mealChildRate: detail.mealChildRate,
        defaultMealPlanId: detail.mealPlanId || undefined,
        defaultMealAdultRate: detail.mealAdultRate,
        defaultMealChildRate: detail.mealChildRate,
        childAgeLimit: detail.childAgeLimit,
        chargeableChildAge: detail.chargeableChildAge,
        infantAgeLimit: detail.infantAgeLimit,
        includeTaxes: detail.includeTaxes ?? false,
      });
    }
  }, [line, category, onAdd, onUpdate, lineKeyOrNew]);

  // Build SyutSelect options from ALL active meal plans for the property
  const mealPlanOptions: SelectOption[] = useMemo(() => {
    return allMealPlans.map(mp => {
      // Check if this meal plan has a matching rate plan detail (for rate display)
      const matchingDetail = selectedGroup?.details.find(d => d.mealPlanId === mp.id);
      const adultRate = matchingDetail ? matchingDetail.mealAdultRate : (mp.adultRate || 0);
      const childRate = matchingDetail ? matchingDetail.mealChildRate : (mp.childRate || 0);
      const rateInfo = adultRate > 0 || childRate > 0
        ? ` (${formatCurrency(adultRate)}/adult · ${formatCurrency(childRate)}/child)`
        : '';
      const defaultTag = matchingDetail?.isDefaultMealPlan ? ' ★ Default' : '';
      return {
        value: mp.id,
        label: `${mp.planCode} - ${mp.planName}${rateInfo}${defaultTag}`,
      };
    });
  }, [allMealPlans, selectedGroup]);

  // Change meal plan — look up matching detail or fall back to meal plan master defaults
  const handleMealPlanChange = useCallback((value: string | string[] | null) => {
    if (!selectedGroup || !line || !value || Array.isArray(value)) return;
    const mealPlanId = value;
    const matchingDetail = selectedGroup.details.find(d => d.mealPlanId === mealPlanId);
    const mealPlan = allMealPlans.find(mp => mp.id === mealPlanId);
    if (!mealPlan) return;

    onUpdate(lineKeyOrNew, {
      ratePlanDetailId: matchingDetail?.ratePlanDetailId || undefined,
      mealPlanId: mealPlan.id,
      mealPlanCode: mealPlan.planCode,
      mealPlanName: mealPlan.planName,
      mealAdultRate: matchingDetail ? matchingDetail.mealAdultRate : (mealPlan.adultRate || 0),
      mealChildRate: matchingDetail ? matchingDetail.mealChildRate : (mealPlan.childRate || 0),
    });
  }, [selectedGroup, line, allMealPlans, lineKeyOrNew, onUpdate]);

  const handleRoomChange = useCallback((newCount: number) => {
    if (newCount <= 0) {
      onRemove(line!.lineKey);
      return;
    }
    if (!line) {
      const bestGroup = grouped.find(g => g.ratePlanId === bestRatePlanId) || grouped[0];
      if (bestGroup) onAdd(category, bestGroup.defaultDetail);
      return;
    }
    // Auto-fill the upgrade count with the new deficit (the agent may raise it
    // further, e.g. to keep a group together); shrink it back when the request
    // no longer exceeds what the deficit demands.
    const newShortage = Math.max(0, newCount - category.availableRooms);
    let newUpgrade = Math.min(line.upgradeRooms ?? 0, newCount);
    if (newShortage > newUpgrade) newUpgrade = newShortage;
    onUpdate(lineKeyOrNew, {
      numberOfRooms: newCount,
      upgradeRooms: newUpgrade > 0 ? newUpgrade : undefined,
      ...(newUpgrade <= 0 ? { allocatedRoomCategoryId: undefined, allocatedCategoryName: undefined } : {}),
    });
  }, [line, grouped, bestRatePlanId, category, onAdd, onUpdate, onRemove, lineKeyOrNew]);

  const handleAdultsChange = useCallback((newVal: number) => {
    const updates: Partial<DetailLineUI> = { numAdults: newVal };
    // Sum cap (adults+children) is bounded by maxOccupancy per room.
    const totalCapacity = category.maxOccupancy * (line?.numberOfRooms || 1);
    const remainingForChildren = totalCapacity - newVal;
    if (line && line.numChildren > remainingForChildren) {
      updates.numChildren = Math.max(0, remainingForChildren);
    }
    onUpdate(lineKeyOrNew, updates);
  }, [category, line, onUpdate, lineKeyOrNew]);

  const handleChildrenChange = useCallback((newVal: number) => {
    onUpdate(lineKeyOrNew, { numChildren: newVal });
  }, [lineKeyOrNew, onUpdate]);

  // Per-line charges dispatched via the active pricing model — matches the
  // summary panel so the inline breakdown and the total always agree.
  const charges = useMemo(
    () => (line ? calcLineByModel(effectiveModel, line, numNights, childAges) : null),
    [effectiveModel, line, numNights, childAges],
  );
  const roomCharges = charges?.roomCharges ?? 0;
  const extraCharges = charges?.extraCharges ?? 0;
  const mealCharges = charges?.mealCharges ?? 0;
  const lineTotal = charges?.lineTotal ?? 0;

  /**
   * Rate-adjustment rows for this line.
   *
   * Hoisted to component scope deliberately. These used to be built inside the
   * adjustment panel's render IIFE, which meant the Line Total below could not
   * see them and showed the RACK amount while the booking summary showed the
   * adjusted one (`sellingSubtotal = subtotal + adjustmentTotal`). The card and
   * the summary therefore disagreed by exactly the adjustment — and the card is
   * the number an agent reads out to a guest.
   *
   * `notApplicable` marks a component that is priced but not in play at this
   * occupancy — an Extra Person rate with nobody beyond base occupancy. Its
   * quantity is 0, so any rate typed against it changes nothing at all.
   */
  const adjustmentRows = useMemo(() => {
    if (!line) return [];
    const rows: {
      key: 'agreedRatePerRoom' | 'agreedMealAdultRate' | 'agreedExtraAdultRate';
      label: string; unit: string; rack: number; qty: number; agreed?: number;
    }[] = [
      // Quantities come from the pricing calculator, never inferred here. Under the
      // bundled model the meal is billed flat on base_occupancy x rooms, so deriving
      // the quantity from actual adults grew the DISCOUNT while the CHARGE stood
      // still and the booking priced lower the fuller it got.
      { key: 'agreedRatePerRoom', label: 'Room Tariff', unit: '/ room-night',
        rack: line.ratePerRoom, qty: charges?.roomUnits ?? 0,
        agreed: line.agreedRatePerRoom },
      { key: 'agreedMealAdultRate', label: 'Meal Plan', unit: '/ adult-night',
        rack: line.mealAdultRate, qty: charges?.mealAdultUnits ?? 0,
        agreed: line.agreedMealAdultRate },
      { key: 'agreedExtraAdultRate', label: 'Extra Person', unit: '/ person-night',
        rack: line.extraAdultRate, qty: charges?.extraAdultUnits ?? 0,
        agreed: line.agreedExtraAdultRate },
    ];
    return rows
      .filter(r => r.rack > 0 || r.agreed != null)
      .map(r => ({
        ...r,
        delta: r.agreed != null ? Math.round((r.agreed - r.rack) * 100) / 100 : 0,
        notApplicable: r.qty === 0,
      }));
  }, [line, charges]);

  /** Signed premium/discount for this line. Negative = discount. */
  const netAdjustment = useMemo(
    () => adjustmentRows.reduce((sum, r) => sum + r.delta * r.qty, 0),
    [adjustmentRows],
  );

  /** What the guest actually pays for this line. */
  const sellingLineTotal = lineTotal + netAdjustment;

  const cardClasses = [
    'room-category-card',
    line && 'selected',
    isSoldOut && 'sold-out',
    hasError && 'form-field-error',
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses}>
      {/* HEADER */}
      <div className="room-category-card-header" onClick={handleHeaderClick}>
        <div className="card-title-row">
          <div className="card-name-group">
            <span className="card-name">{category.categoryName}</span>
            {compact ? (
              /* The availability count belongs to the category, not the line —
                 repeating it here would read as extra inventory. */
              <span className="occupancy-line-badge">
                Occupancy line {occupancyIndex + 1}
              </span>
            ) : (
              <span className={`avail-badge-lg ${getAvailBadgeClass(category.availableRooms, category.totalRooms)}`}>
                {category.availableRooms}/{category.totalRooms} {category.availableRooms > 0 ? 'available' : 'rooms'}
              </span>
            )}
          </div>
          <div className="card-badges">
            {isSoldOut && <span className="badge-sold-out">Sold Out</span>}
            {isLowAvail && (
              <span className="badge-low-avail">
                <IconSystem.alert size={10} /> Last {category.availableRooms}!
              </span>
            )}
          </div>
        </div>
        <div className="card-meta">
          <span>
            {category.categoryCode} · Base {category.baseOccupancy} / Max {category.maxOccupancy} pax
            {uniqueRatePlanCount > 0 && ` · ${uniqueRatePlanCount} rate plan${uniqueRatePlanCount !== 1 ? 's' : ''}`}
          </span>
          {!line && canSelect && (
            <button
              type="button"
              className="add-btn"
              onClick={(e) => { e.stopPropagation(); handleQuickAdd(); }}
            >
              <IconSystem.add size={12} />{' '}
              {!isSoldOut ? 'Add' : hasUpgradeTargets ? 'Add with upgrade' : 'Add as oversell'}
            </button>
          )}
          {!line && !canSelect && (
            /* No upgrade target and no permission to carry the shortage. The card
               used to sit here dimmed and silent, which reads as a broken button
               rather than a closed door. */
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              Sold out — no other category has space for these dates
            </span>
          )}
          {line && (
            <button
              type="button"
              className="remove-btn"
              onClick={(e) => { e.stopPropagation(); onRemove(line!.lineKey); }}
              title="Remove room"
            >
              <IconSystem.x size={12} /> Remove
            </button>
          )}
        </div>

        {/* The validation message itself, not just a red border. Sits in the
            always-visible header so it is readable whether or not the card is
            expanded — a save refused with "fix the highlighted errors" has to
            say which error, and where. */}
        {errorMessage && (
          <div className="form-error-message" style={{ marginTop: '0.3rem' }}>
            {errorMessage}
          </div>
        )}
      </div>

      {/* BODY — expandable */}
      <div className={`room-category-card-body ${isExpanded ? 'expanded' : ''}`}>
        <div className="room-category-card-body-inner">
          {grouped.length > 0 ? (
            <>
              {/* Rate Plan pills — one per unique rate plan */}
              <div className="rate-plan-list">
                <div className="rate-plan-list-label">Rate Plans</div>
                {grouped.map(group => {
                  const isSelected = line?.ratePlanId === group.ratePlanId;
                  // Headline rate = room rate + default meal plan for base_occupancy pax.
                  // Under bundled pricing this is the literal flat charge; under a_la_carte
                  // it's the "standard pax" price (1 adult filling base_occupancy would pay this).
                  const defaultMealAdult = group.defaultDetail.mealAdultRate || 0;
                  const defaultMealCode = group.defaultDetail.mealPlanCode || '';
                  const headlineRate = group.ratePerRoom + defaultMealAdult * category.baseOccupancy;
                  const mealSuffix = defaultMealCode
                    ? ` · Room + ${defaultMealCode} × ${category.baseOccupancy} pax`
                    : '';
                  return (
                    <div
                      key={group.ratePlanId}
                      className={`rate-plan-pill ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleRatePlanSelect(group)}
                    >
                      <div className="pill-radio">
                        {isSelected
                          ? <IconSystem.checkCircle size={14} />
                          : <div className="pill-radio-empty" />
                        }
                      </div>
                      <div className="pill-content">
                        <div className="pill-name">
                          {group.rateName}
                          {group.rateType && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.25rem' }}>({group.rateType})</span>}
                          {group.ratePlanId === bestRatePlanId && (
                            <span className="badge-best-rate">
                              <IconSystem.star size={9} /> Best Rate
                            </span>
                          )}
                        </div>
                        <div className="pill-rate">
                          {formatCurrency(headlineRate)}/night
                          <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                            {mealSuffix} · {group.details.length} meal plan{group.details.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Meal Plan dropdown — always visible when a rate plan is selected */}
              {line && selectedGroup && (
                <div className="meal-plan-selector">
                  <SyutSelect
                    label="Meal Plan"
                    options={mealPlanOptions}
                    value={line.mealPlanId || ''}
                    onChange={handleMealPlanChange}
                    size="sm"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="no-rates-msg">No rate plans available for this category</div>
          )}

          {line && (
            <>
              <div className="room-controls-row">
                <StepperControl
                  label="Rooms"
                  value={line.numberOfRooms}
                  min={0}
                  max={100}
                  onChange={handleRoomChange}
                  editable
                />
                <StepperControl
                  label="Adults"
                  value={line.numAdults}
                  min={1}
                  max={Math.min(category.maxAdults, category.maxOccupancy) * line.numberOfRooms}
                  onChange={handleAdultsChange}
                  editable
                />
                <StepperControl
                  label="Children"
                  value={line.numChildren}
                  min={0}
                  max={Math.min(
                    category.maxChildren * line.numberOfRooms,
                    Math.max(0, category.maxOccupancy * line.numberOfRooms - line.numAdults),
                  )}
                  onChange={handleChildrenChange}
                  editable
                />
              </div>

              {/* Upgrade split (E1) — shortage-driven or voluntary. Guest pays this
                  category's rate; upgraded rooms consume the target's inventory. */}
              {(shortage > 0 || upgradeRooms > 0 || upgradeOptions.length > 0) && (
                <div
                  style={{
                    marginTop: '0.6rem', padding: '0.6rem 0.75rem', borderRadius: '8px',
                    border: `1px solid ${upgradeError ? 'var(--form-border-error, #ef4444)' : 'var(--border-color)'}`,
                    background: shortage > 0 ? 'rgba(217,119,6,0.06)' : 'var(--bg-secondary)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', fontWeight: 650, marginBottom: shortage > 0 || upgradeRooms > 0 ? '0.5rem' : 0 }}>
                    <IconSystem.alert size={13} style={{ color: shortage > 0 ? '#d97706' : 'var(--text-muted)' }} />
                    {shortage > 0
                      ? `${category.categoryName} has only ${category.availableRooms} of the ${line.numberOfRooms} requested — ${
                          canOversell && !hasUpgradeTargets
                            ? 'choose how to cover the rest:'
                            : 'upgrade the rest:'}`
                      : 'Room Upgrade (optional)'}
                  </div>

                  {/* Two genuinely different answers to a shortage, so the desk
                      picks one explicitly rather than discovering at save which
                      one the form assumed. Upgrading costs the property a better
                      room; carrying the shortage costs it a room it does not
                      have. Only offered when the user may actually do it. */}
                  {shortage > 0 && canOversell && (
                    <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.55rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className={`btn btn-sm ${line.acceptShortage ? 'btn-secondary' : 'btn-primary'}`}
                        disabled={!hasUpgradeTargets}
                        title={hasUpgradeTargets ? undefined : 'No other category has space for these dates'}
                        onClick={() => onUpdate(lineKeyOrNew, { acceptShortage: undefined })}
                      >
                        Upgrade {shortage} room{shortage !== 1 ? 's' : ''}
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${line.acceptShortage ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={() => onUpdate(lineKeyOrNew, {
                          acceptShortage: true,
                          // Carrying the shortage means these rooms stay in the
                          // booked category — any upgrade allocation would
                          // contradict that, so it is cleared here rather than
                          // left to confuse the payload builder.
                          upgradeRooms: undefined,
                          allocatedRoomCategoryId: undefined,
                          allocatedCategoryName: undefined,
                        })}
                      >
                        Accept shortage (oversell)
                      </button>
                    </div>
                  )}

                  {line.acceptShortage && (
                    <div style={{ fontSize: '0.72rem', color: '#dc2626', marginBottom: '0.5rem', fontWeight: 600 }}>
                      {shortage} room{shortage !== 1 ? 's' : ''} will be committed beyond {category.categoryName}&apos;s
                      inventory. You will be asked to record why before the booking saves, and the
                      shortage appears on the Shortage Board until it is resolved.
                    </div>
                  )}
                  <div
                    style={{ display: line.acceptShortage ? 'none' : 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}
                  >
                    <StepperControl
                      label="Rooms to upgrade"
                      value={upgradeRooms}
                      // The floor is the shortage only while the shortage is
                      // being upgraded away; carrying it makes 0 legitimate.
                      min={line.acceptShortage ? 0 : shortage}
                      max={line.numberOfRooms}
                      onChange={handleUpgradeCountChange}
                      editable
                    />
                    {upgradeRooms > 0 && (
                      <div style={{ flex: 1, minWidth: '220px' }}>
                        <SyutSelect
                          label={<>Host in <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(guest pays {category.categoryName} rate)</span></>}
                          options={upgradeOptions}
                          value={line.allocatedRoomCategoryId || ''}
                          onChange={handleUpgradeTargetChange}
                          placeholder="Select upgrade category…"
                          size="sm"
                        />
                      </div>
                    )}
                  </div>
                  {upgradeError && <div className="form-error-message">{upgradeError}</div>}
                  {!upgradeError && upgradeRooms > 0 && line.allocatedRoomCategoryId && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>
                      {baseRooms > 0
                        ? `${baseRooms} room${baseRooms !== 1 ? 's' : ''} stay in ${category.categoryName}; ${upgradeRooms} hosted in ${line.allocatedCategoryName}.`
                        : `All ${upgradeRooms} room${upgradeRooms !== 1 ? 's' : ''} hosted in ${line.allocatedCategoryName}.`}
                      {' '}Reports and rate stay on {category.categoryName}. Approval may be required per property policy.
                    </div>
                  )}
                </div>
              )}

              {/* ── Per-pax package entry (group bookings) ───────────────── */}
              {isPerPax && (
                <div className="per-pax-panel">
                  <div className="per-pax-fields">
                    <div className="form-group">
                      <label className="form-label">Rate / Pax / Night</label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        className="form-input"
                        value={line.ratePerPax ?? ''}
                        placeholder="0.00"
                        onChange={e => onUpdate(line.lineKey, {
                          ratePerPax: e.target.value === '' ? undefined : Number(e.target.value),
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Occupancy Remark</label>
                      <input
                        type="text"
                        maxLength={100}
                        className="form-input"
                        value={line.occupancyRemark ?? ''}
                        placeholder="e.g. Double Sharing"
                        onChange={e => onUpdate(line.lineKey, { occupancyRemark: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Derived split — staff verify the room/meal division at a
                      glance instead of doing the arithmetic themselves. Mirrors
                      the backend PerPaxStrategy, which is the figure that gets
                      stored. */}
                  {(line.ratePerPax ?? 0) > 0 && (() => {
                    const pax = (line.numAdults || 0) + (line.numChildren || 0);
                    const perPaxMeal = pax > 0 ? mealCharges / pax / (numNights || 1) : 0;
                    const perPaxRoom = (line.ratePerPax ?? 0) - perPaxMeal;
                    return (
                      <div className="per-pax-derived">
                        <span>
                          {formatCurrency(line.ratePerPax ?? 0)}/pax
                          {perPaxMeal > 0 && <> − {formatCurrency(perPaxMeal)} meal = {formatCurrency(Math.max(0, perPaxRoom))} room</>}
                        </span>
                        <span className="per-pax-derived-total">
                          {pax} pax × {numNights} night{numNights !== 1 ? 's' : ''} = {formatCurrency(lineTotal)}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="room-price-breakdown">
                {isPerPax ? (
                  <div className="price-line">
                    Room revenue (package less meal plan) = {formatCurrency(roomCharges)}
                  </div>
                ) : (
                  <div className="price-line">
                    {numNights} night{numNights !== 1 ? 's' : ''} × {line.numberOfRooms} room{line.numberOfRooms !== 1 ? 's' : ''} × {formatCurrency(line.ratePerRoom)} = {formatCurrency(roomCharges)}
                  </div>
                )}

                {isPerPax ? (
                  /* Per-pax suppresses the extra-person breakdown entirely: the
                     rate IS the per-person charge, so extras are structurally
                     zero and showing an empty section would imply otherwise. */
                  mealCharges > 0 ? (
                    <div className="price-line">
                      Meal plan{line.mealPlanCode ? ` (${line.mealPlanCode})` : ''} = {formatCurrency(mealCharges)}
                    </div>
                  ) : null
                ) : isBundled ? (() => {
                  // -------- Bundled breakdown --------
                  // meal_bundle = default.adult × base_occupancy × rooms × nights (always charged)
                  // meal_diff   = base_pax × (selected - default) × nights   (only if plan differs)
                  // extras      = extra_pax × (extra_room + selected_meal) × nights
                  const baseOcc = line.baseOccupancy || 2;
                  const rooms = line.numberOfRooms || 1;
                  const baseCapacity = baseOcc * rooms;
                  const defaultAdult = line.defaultMealAdultRate ?? 0;
                  const defaultChild = line.defaultMealChildRate ?? 0;
                  const selectedAdult = line.mealAdultRate || 0;
                  const selectedChild = line.mealChildRate || 0;

                  // Classify pax for diff computation
                  let totalAdults = line.numAdults || 0;
                  let chargeable: number;
                  let freeCount = 0;
                  if (childAges && childAges.length > 0) {
                    const cls = classifyChildrenByAge(childAges, line.childAgeLimit ?? 12, line.chargeableChildAge ?? 6, line.infantAgeLimit ?? 5);
                    totalAdults += cls.childrenAsAdults;
                    chargeable = cls.chargeableChildren;
                    freeCount = cls.freeChildren;
                  } else {
                    chargeable = line.numChildren || 0;
                  }
                  const baseAdults = Math.min(totalAdults, baseCapacity);
                  const baseChildren = Math.min(chargeable, Math.max(0, baseCapacity - baseAdults));
                  const extraAdults = Math.max(0, totalAdults - baseCapacity);
                  const extraChildren = Math.max(0, chargeable - baseChildren);

                  const bundleAmount = defaultAdult * baseCapacity * numNights;
                  // Clamp at zero — downgrading the meal plan does NOT refund the bundle.
                  // The bundle headline price is the contractual floor; only upgrades add cost.
                  const diffAmount = Math.max(
                    0,
                    (baseAdults * (selectedAdult - defaultAdult)
                      + baseChildren * (selectedChild - defaultChild)) * numNights
                  );
                  const extrasRoomAmount = (extraAdults * line.extraAdultRate + extraChildren * line.extraChildRate) * numNights;
                  const extrasMealAmount = (extraAdults * selectedAdult + extraChildren * selectedChild) * numNights;
                  const defaultCode = (line as { defaultMealPlanCode?: string }).defaultMealPlanCode || line.mealPlanCode || 'default';

                  return (
                    <>
                      {bundleAmount > 0 && (
                        <div className="price-line extra">
                          + Meal bundle ({defaultCode}): {baseCapacity} pax × {formatCurrency(defaultAdult)} × {numNights} night{numNights !== 1 ? 's' : ''} = {formatCurrency(bundleAmount)}
                        </div>
                      )}
                      {diffAmount > 0 && (
                        <div className="price-line extra">
                          + Meal upgrade{line.mealPlanCode ? ` (${line.mealPlanCode})` : ''}: {baseAdults} adult{baseAdults !== 1 ? 's' : ''} × {formatCurrency(selectedAdult - defaultAdult)}
                          {baseChildren > 0 ? ` + ${baseChildren} child${baseChildren !== 1 ? 'ren' : ''} × ${formatCurrency(selectedChild - defaultChild)}` : ''}
                          {numNights !== 1 ? ` × ${numNights} nights` : ''} = {formatCurrency(diffAmount)}
                        </div>
                      )}
                      {extrasMealAmount > 0 && (() => {
                        const parts: string[] = [];
                        if (extraAdults > 0) parts.push(`${extraAdults} extra adult${extraAdults !== 1 ? 's' : ''} × ${formatCurrency(selectedAdult)}`);
                        if (extraChildren > 0) parts.push(`${extraChildren} extra child${extraChildren !== 1 ? 'ren' : ''} × ${formatCurrency(selectedChild)}`);
                        return (
                          <div className="price-line extra">
                            + Extra pax meal{line.mealPlanCode ? ` (${line.mealPlanCode})` : ''}: {parts.join(' + ')}
                            {numNights !== 1 ? ` × ${numNights} nights` : ''} = {formatCurrency(extrasMealAmount)}
                          </div>
                        );
                      })()}
                      {extrasRoomAmount > 0 && (() => {
                        const parts: string[] = [];
                        if (extraAdults > 0) parts.push(`${extraAdults} extra adult${extraAdults !== 1 ? 's' : ''} × ${formatCurrency(line.extraAdultRate)}`);
                        if (extraChildren > 0) parts.push(`${extraChildren} extra child${extraChildren !== 1 ? 'ren' : ''} × ${formatCurrency(line.extraChildRate)}`);
                        return (
                          <div className="price-line extra">
                            + Extra pax room: {parts.join(' + ')}
                            {numNights !== 1 ? ` × ${numNights} nights` : ''} = {formatCurrency(extrasRoomAmount)}
                          </div>
                        );
                      })()}
                      {freeCount > 0 && (
                        <div className="price-line extra">
                          {freeCount} infant{freeCount !== 1 ? 's' : ''} free
                        </div>
                      )}
                    </>
                  );
                })() : (
                  <>
                    {extraCharges > 0 && (() => {
                      const baseOcc = line.baseOccupancy || 2;
                      const rooms = line.numberOfRooms || 1;
                      let exAdults = Math.max(0, (line.numAdults || 0) - baseOcc * rooms);
                      let exChildren: number;
                      let freeCount = 0;
                      if (childAges && childAges.length > 0) {
                        const cls = classifyChildrenByAge(childAges, line.childAgeLimit ?? 12, line.chargeableChildAge ?? 6, line.infantAgeLimit ?? 5);
                        exAdults += cls.childrenAsAdults;
                        exChildren = cls.chargeableChildren;
                        freeCount = cls.freeChildren;
                      } else {
                        exChildren = line.numChildren || 0;
                      }
                      const parts: string[] = [];
                      if (exAdults > 0) parts.push(`${exAdults} extra adult${exAdults !== 1 ? 's' : ''} × ${formatCurrency(line.extraAdultRate)}`);
                      if (exChildren > 0) parts.push(`${exChildren} child${exChildren !== 1 ? 'ren' : ''} × ${formatCurrency(line.extraChildRate)}`);
                      if (freeCount > 0) parts.push(`${freeCount} infant${freeCount !== 1 ? 's' : ''} free`);
                      return (
                        <div className="price-line extra">+ Extra charges: {parts.join(' + ')} = {formatCurrency(extraCharges)}</div>
                      );
                    })()}
                    {mealCharges > 0 && (() => {
                      const mealParts: string[] = [];
                      if (line.mealAdultRate > 0) mealParts.push(`${line.numAdults} adult${line.numAdults !== 1 ? 's' : ''} × ${formatCurrency(line.mealAdultRate)}`);
                      if (line.mealChildRate > 0 && line.numChildren > 0) mealParts.push(`${line.numChildren} child${line.numChildren !== 1 ? 'ren' : ''} × ${formatCurrency(line.mealChildRate)}`);
                      return (
                        <div className="price-line extra">+ Meal charges{line.mealPlanCode ? ` (${line.mealPlanCode})` : ''}: {mealParts.join(' + ')} = {formatCurrency(mealCharges)}</div>
                      );
                    })()}
                  </>
                )}

                {/* ── Rate adjustment — the operator types the AGREED rate;
                       the rack rate above stays untouched and the premium /
                       discount is derived. Reason is mandatory on discounts. ── */}
                {(() => {
                  const deltas = adjustmentRows;
                  if (deltas.length === 0) return null;
                  const netAdj = netAdjustment;
                  const hasDiscount = deltas.some(r => r.delta < 0);
                  const hasAny = deltas.some(r => r.delta !== 0);
                  const reasonMissing = hasDiscount && !(line.adjustmentRemarks || '').trim();

                  return (
                    <div style={{
                      marginTop: '0.4rem', paddingTop: '0.4rem',
                      borderTop: '1px dashed var(--border-color)',
                    }}>
                      <button
                        type="button"
                        onClick={() => setAdjustOpen(o => !o)}
                        style={{
                          border: 'none', background: 'none', padding: 0, cursor: 'pointer',
                          fontSize: '0.75rem', fontWeight: 600, color: '#2563eb',
                          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                        }}
                      >
                        {adjustOpen ? 'Hide rate adjustment' : 'Adjust rates'}
                        {hasAny && (
                          <span style={{
                            fontSize: '0.7rem', padding: '0.125rem 0.4rem', borderRadius: '0.25rem',
                            fontWeight: 600,
                            background: netAdj < 0 ? '#fee2e2' : '#fef3c7',
                            color: netAdj < 0 ? '#991b1b' : '#92400e',
                          }}>
                            {netAdj >= 0 ? '+' : '−'}{formatCurrency(Math.abs(netAdj))}
                          </span>
                        )}
                        {reasonMissing && (
                          <span style={{ fontSize: '0.7rem', color: '#b91c1c', fontWeight: 600 }}>
                            reason required
                          </span>
                        )}
                      </button>

                      {adjustOpen && (
                        <div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                          {deltas.map(r => (
                            <div key={r.key} style={{
                              display: 'grid',
                              gridTemplateColumns: 'minmax(0,1.2fr) 1fr 1fr minmax(0,1.1fr)',
                              gap: '0.4rem', alignItems: 'center', fontSize: '0.75rem',
                            }}>
                              <span style={{ color: r.notApplicable ? 'var(--text-muted)' : 'var(--text-secondary)' }}>
                                {r.label}
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem' }}> {r.unit}</span>
                                {/* A priced component with nothing to price. Left VISIBLE rather
                                    than hidden: removing the row makes the rate plan look as
                                    though it has no such rate, which is a different and wrong
                                    message. */}
                                {r.notApplicable && (
                                  <span style={{ display: 'block', fontSize: '0.65rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                    {r.agreed != null
                                      ? 'no longer applies — clear to tidy up'
                                      : 'not applicable at this occupancy'}
                                  </span>
                                )}
                              </span>
                              <span style={{
                                textAlign: 'right', color: 'var(--text-muted)',
                                fontVariantNumeric: 'tabular-nums',
                              }} title="Rack rate — never modified">
                                {formatCurrency(r.rack)}
                              </span>
                              <input
                                type="number"
                                min={0}
                                className="form-input"
                                /* Inert AND empty → disabled: editing it can only mislead,
                                   since qty is 0 and the delta is multiplied by 0. Inert but
                                   already carrying a saved rate stays editable, or a value
                                   left behind by a pax reduction could never be cleared. */
                                disabled={r.notApplicable && r.agreed == null}
                                title={r.notApplicable
                                  ? 'No charge for this component at the current occupancy, so a rate here would change nothing.'
                                  : 'Agreed rate — what the guest is actually charged'}
                                style={{
                                  textAlign: 'right', fontSize: '0.75rem',
                                  padding: '0.2rem 0.4rem', fontVariantNumeric: 'tabular-nums',
                                  fontWeight: r.delta !== 0 ? 700 : 400,
                                  ...(r.notApplicable && r.agreed == null
                                    ? { opacity: 0.5, cursor: 'not-allowed' }
                                    : {}),
                                }}
                                value={r.agreed ?? r.rack}
                                onChange={e => {
                                  const raw = e.target.value;
                                  const num = raw === '' ? undefined : Math.max(0, Number(raw) || 0);
                                  onUpdate(line.lineKey, {
                                    [r.key]: num === undefined || num === r.rack ? undefined : num,
                                  } as Partial<DetailLineUI>);
                                }}
                              />
                              <span style={{
                                textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                                color: r.delta === 0 ? 'var(--text-muted)'
                                  : r.delta < 0 ? '#b91c1c' : '#b45309',
                              }}>
                                {r.delta === 0 ? '—' : (
                                  <>
                                    {r.delta > 0 ? '+' : '−'}{formatCurrency(Math.abs(r.delta * r.qty))}
                                    {r.rack > 0 && (
                                      <span style={{ fontSize: '0.65rem', fontWeight: 500, display: 'block' }}>
                                        {r.delta > 0 ? '+' : '−'}{Math.abs(Math.round((r.delta / r.rack) * 1000) / 10)}%
                                      </span>
                                    )}
                                  </>
                                )}
                              </span>
                            </div>
                          ))}
                          {hasAny && (
                            <input
                              type="text"
                              className={`form-input${reasonMissing ? ' form-field-error' : ''}`}
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                              maxLength={200}
                              value={line.adjustmentRemarks ?? ''}
                              onChange={e => onUpdate(line.lineKey, { adjustmentRemarks: e.target.value })}
                              placeholder={hasDiscount
                                ? 'Reason for the discount (required)…'
                                : 'Reason (optional)…'}
                            />
                          )}
                          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                            Rack rates stay on record; only the difference is stored, so Rooms / F&amp;B revenue
                            stays department-accurate.
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* What the guest pays. The rack figure stays visible beside it —
                    "rack rates stay on record" is the whole premise of the
                    adjustment design, so the card must not hide the number the
                    discount was struck against. */}
                <div className="price-total">
                  Line Total: {formatCurrency(sellingLineTotal)}
                  {netAdjustment !== 0 && (
                    <span style={{ fontWeight: 400, fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: '0.4rem' }}>
                      (rack {formatCurrency(lineTotal)}
                      {' '}
                      <span style={{ color: netAdjustment < 0 ? '#b91c1c' : '#b45309', fontWeight: 600 }}>
                        {netAdjustment > 0 ? '+' : '−'}{formatCurrency(Math.abs(netAdjustment))}
                      </span>
                      )
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN SECTION COMPONENT
// ============================================================================

/**
 * Client-side line identity. A counter rather than a random id so the value is
 * reproducible in tests and readable while debugging; it never leaves the form.
 */
let lineKeySeq = 0;
const makeLineKey = () => `ln-${++lineKeySeq}`;

const RoomRateSection: React.FC<RoomRateSectionProps> = ({
  availabilityResult, detailLines, onDetailLinesChange, numNights, fieldErrors, checkingAvailability,
  propertyId, checkInDate, checkOutDate, childAges, pricingModel, canOversell = false,
  commercialLines, onCommercialLinesChange, totalRooms, onTotalRoomsChange,
}) => {
  // Memoised: `?? []` would hand out a fresh array every render, making every
  // downstream useMemo that depends on it recompute for nothing.
  const categories = useMemo(
    () => availabilityResult?.categories ?? [],
    [availabilityResult],
  );

  // Booking-level rate basis. Seeded from the loaded lines on edit/copy so an
  // existing group booking reopens in the mode it was entered in.
  const [rateBasis, setRateBasis] = useState<'PER_ROOM' | 'PER_PAX'>(
    detailLines.some(l => l.rateBasis === 'PER_PAX') ? 'PER_PAX' : 'PER_ROOM',
  );


  /** Availability calendar is reference — closed by default so the grid leads. */
  const [availOpen, setAvailOpen] = useState(false);

  const hydratedRef = useRef(false);
  useEffect(() => {
    // Re-seed once per load: the lines arrive asynchronously on edit, after the
    // initial state above has already been computed from an empty array.
    if (hydratedRef.current || detailLines.length === 0) return;
    hydratedRef.current = true;
    setRateBasis(detailLines.some(l => l.rateBasis === 'PER_PAX') ? 'PER_PAX' : 'PER_ROOM');
  }, [detailLines]);

  // Load ALL active meal plans for the property
  const [allMealPlans, setAllMealPlans] = useState<MealPlan[]>([]);
  useEffect(() => {
    if (!propertyId) { setAllMealPlans([]); return; }
    let cancelled = false;
    mealPlanService.getAll({ propertyId, pageSize: 500, includeInactive: false })
      .then(res => { if (!cancelled) setAllMealPlans(res.data || []); })
      .catch(() => { if (!cancelled) setAllMealPlans([]); });
    return () => { cancelled = true; };
  }, [propertyId]);

  // --- Add line for a category ---
  // The `ratePlan` passed in is always the default meal plan detail for the chosen
  // rate plan (both caller sites pass `group.defaultDetail`), so its meal rates double
  // as the default-meal snapshot used by bundled pricing.
  const handleAddLine = useCallback((cat: CategoryAvailability, ratePlan: RatePlanOption) => {
    const newLine: DetailLineUI = {
      lineKey: makeLineKey(),
      rateBasis,
      roomCategoryId: cat.roomCategoryId,
      categoryName: cat.categoryName,
      categoryCode: cat.categoryCode,
      numberOfRooms: 1,
      // Sold-out category: the single room already needs an upgrade target.
      upgradeRooms: cat.availableRooms <= 0 ? 1 : undefined,
      ratePlanId: ratePlan.ratePlanId,
      ratePlanDetailId: ratePlan.ratePlanDetailId,
      ratePlanName: ratePlan.rateName,
      mealPlanId: ratePlan.mealPlanId || undefined,
      mealPlanCode: ratePlan.mealPlanCode || '',
      mealPlanName: ratePlan.mealPlanName || '',
      ratePerRoom: ratePlan.ratePerRoom,
      extraAdultRate: ratePlan.extraAdultRate,
      extraChildRate: ratePlan.extraChildRate,
      mealAdultRate: ratePlan.mealAdultRate,
      mealChildRate: ratePlan.mealChildRate,
      defaultMealPlanId: ratePlan.mealPlanId || undefined,
      defaultMealAdultRate: ratePlan.mealAdultRate,
      defaultMealChildRate: ratePlan.mealChildRate,
      numAdults: cat.baseOccupancy || 2,
      numChildren: 0,
      baseOccupancy: cat.baseOccupancy || 2,
      childAgeLimit: ratePlan.childAgeLimit,
      chargeableChildAge: ratePlan.chargeableChildAge,
      infantAgeLimit: ratePlan.infantAgeLimit,
      includeTaxes: ratePlan.includeTaxes ?? false,
    };
    onDetailLinesChange([...detailLines, newLine]);
  }, [detailLines, onDetailLinesChange, rateBasis]);

  // --- Update a single line by its own key ---
  const handleUpdateLine = useCallback((lineKey: string, updates: Partial<DetailLineUI>) => {
    onDetailLinesChange(detailLines.map(l =>
      l.lineKey === lineKey ? { ...l, ...updates } : l
    ));
  }, [detailLines, onDetailLinesChange]);

  // --- Remove a single line by its own key ---
  const handleRemoveLine = useCallback((lineKey: string) => {
    onDetailLinesChange(detailLines.filter(l => l.lineKey !== lineKey));
  }, [detailLines, onDetailLinesChange]);


  /**
   * Booking-level basis switch. Applied to every line at once because a booking
   * that mixed the two would produce an incoherent summary and invoice — the
   * agreed decision in the group-pricing design.
   */
  const handleRateBasisChange = useCallback((basis: 'PER_ROOM' | 'PER_PAX') => {
    setRateBasis(basis);
    if (detailLines.length > 0) {
      onDetailLinesChange(detailLines.map(l => ({ ...l, rateBasis: basis })));
    }
    // Back to Per Room clears the commercial state. The payload keys off
    // commercialLines being present — leaving them populated would make a
    // per-room booking price from lines the operator thinks they abandoned.
    if (basis === 'PER_ROOM') {
      onCommercialLinesChange([]);
      onTotalRoomsChange('');
    }
  }, [detailLines, onDetailLinesChange, onCommercialLinesChange, onTotalRoomsChange]);



  /**
   * Per-pax allocation edit: one detail line per category, count only. The line
   * still carries the category's best rate plan so switching back to Per Room
   * leaves sane rows — but under PER_PAX those rates are vestigial; the money
   * is on the commercial lines and the server prices from them.
   */
  const handleAllocationChange = useCallback((cat: CategoryAvailability, rooms: number) => {
    const existing = detailLines.find((l) => l.roomCategoryId === cat.roomCategoryId);
    if (rooms <= 0) {
      if (existing) onDetailLinesChange(detailLines.filter((l) => l.roomCategoryId !== cat.roomCategoryId));
      return;
    }
    if (existing) {
      onDetailLinesChange(detailLines.map((l) =>
        l.roomCategoryId === cat.roomCategoryId ? { ...l, numberOfRooms: rooms } : l));
      return;
    }
    const best = [...(cat.ratePlans ?? [])].sort((a, b) => a.ratePerRoom - b.ratePerRoom)[0];
    onDetailLinesChange([...detailLines, {
      lineKey: makeLineKey(),
      rateBasis: 'PER_PAX',
      roomCategoryId: cat.roomCategoryId,
      categoryName: cat.categoryName,
      categoryCode: cat.categoryCode,
      numberOfRooms: rooms,
      ratePlanId: best?.ratePlanId,
      ratePlanDetailId: best?.ratePlanDetailId,
      ratePlanName: best?.rateName ?? '',
      mealPlanId: best?.mealPlanId || undefined,
      mealPlanCode: best?.mealPlanCode || '',
      mealPlanName: best?.mealPlanName || '',
      ratePerRoom: best?.ratePerRoom ?? 0,
      extraAdultRate: best?.extraAdultRate ?? 0,
      extraChildRate: best?.extraChildRate ?? 0,
      mealAdultRate: best?.mealAdultRate ?? 0,
      mealChildRate: best?.mealChildRate ?? 0,
      defaultMealPlanId: best?.mealPlanId || undefined,
      defaultMealAdultRate: best?.mealAdultRate,
      defaultMealChildRate: best?.mealChildRate,
      // Pax are distributed across allocation lines at SAVE time from the
      // commercial totals — a live split would fight the operator's typing.
      numAdults: 0,
      numChildren: 0,
      baseOccupancy: cat.baseOccupancy || 2,
      includeTaxes: best?.includeTaxes ?? false,
    }]);
  }, [detailLines, onDetailLinesChange]);

  return (
    <div>
      <hr className="booking-section-divider" />
      <div className="booking-section-title">
        <IconSystem.home size={15} />
        Room & Rate
        {fieldErrors['detailLines'] && (
          <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--danger)', marginLeft: 'auto' }}>
            {fieldErrors['detailLines']}
          </span>
        )}
      </div>

      {/* Rate entry basis — booking-level (see CRS-Group-Package-Pricing-Design §4). */}
      <div className="rate-basis-toggle">
        <span className="rate-basis-label">Rate entry</span>
        <div className="rate-basis-options">
          {(['PER_ROOM', 'PER_PAX'] as const).map(opt => (
            <button
              key={opt}
              type="button"
              className={`rate-basis-opt${rateBasis === opt ? ' is-on' : ''}`}
              onClick={() => handleRateBasisChange(opt)}
            >
              {opt === 'PER_ROOM' ? 'Per Room' : 'Per Pax'}
            </button>
          ))}
        </div>
        {rateBasis === 'PER_PAX' && (
          <span className="rate-basis-hint">
            Package rate per person per night. The meal plan is deducted first; the balance is room revenue.
          </span>
        )}
      </div>

      {/* ── PER-PAX: commercial lines + allocation, the model the backend
             prices from (CRS-Per-Pax-Commercial-Allocation-Design). The cards
             below are per-room only: they fuse price and category on one row,
             which is exactly what this branch exists to separate. ── */}
      {rateBasis === 'PER_PAX' && availabilityResult && categories.length > 0 && (
        <div className={checkingAvailability ? 'room-cards-loading' : undefined}>
          {/* The stated total is what makes the shortfall check POSSIBLE:
              derive it from the allocation instead and whatever was allocated
              is the booking by definition — a short group saves silently and
              surfaces with a guest at the desk. Same rule the server enforces. */}
          <div className="req-line">
            <span>Group requires</span>
            <input
              type="number" min={1} className={`form-input req-num${fieldErrors['totalRooms'] ? ' form-field-error' : ''}`}
              value={totalRooms}
              onChange={(e) => onTotalRoomsChange(e.target.value)}
              placeholder="—"
            />
            <span>rooms</span>
            {(() => {
              const stated = parseInt(totalRooms, 10) || 0;
              const allocated = detailLines.reduce((a, l) => a + (l.numberOfRooms || 0), 0);
              const short = stated - allocated;
              return (
                <span className="req-tally">
                  Allocated <b>{allocated}</b>
                  {stated > 0 && short > 0 && (
                    <span className="req-short"> · {short} room{short !== 1 ? 's' : ''} short</span>
                  )}
                  {stated > 0 && short < 0 && (
                    <span className="req-short"> · {-short} over</span>
                  )}
                  {stated > 0 && short === 0 && <span className="req-ok"> ✓</span>}
                </span>
              );
            })()}
          </div>
          {fieldErrors['totalRooms'] && (
            <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{fieldErrors['totalRooms']}</div>
          )}

          <CommercialLinesSection
            lines={commercialLines}
            onChange={onCommercialLinesChange}
            allMealPlans={allMealPlans}
            numNights={Math.max(1, numNights)}
            fieldErrors={fieldErrors}
          />
          {commercialLines.length === 0 && (
            <button
              type="button"
              className="cl-add"
              onClick={() => onCommercialLinesChange([emptyCommercialLine()])}
            >
              <IconSystem.add size={12} /> Start with a line
            </button>
          )}

          {/* ── Allocation — where the rooms sit. Deliberately cannot touch the
                 money: moving five rooms from Deluxe to Premium is an upgrade,
                 not a re-price. ── */}
          <div className="booking-section-title" style={{ marginTop: '0.75rem' }}>
            <IconSystem.home size={15} />
            Room Allocation
            <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 'auto' }}>
              Distribute the rooms — the rate does not change with the category
            </span>
          </div>
          {fieldErrors['allocation'] && (
            <div className="form-error-message" style={{ marginBottom: '0.4rem' }}>{fieldErrors['allocation']}</div>
          )}
          <div className="alloc-list">
            {categories.map((cat) => {
              const line = detailLines.find((l) => l.roomCategoryId === cat.roomCategoryId);
              const rooms = line?.numberOfRooms ?? 0;
              return (
                <div className="alloc-row" key={cat.roomCategoryId}>
                  <span className="alloc-name">
                    <b>{cat.categoryName}</b>
                    <span className="alloc-meta">
                      {' '}· {cat.availableRooms} available · sleeps {cat.baseOccupancy}
                      {cat.maxOccupancy > cat.baseOccupancy ? `–${cat.maxOccupancy}` : ''}
                    </span>
                  </span>
                  {rooms > cat.availableRooms && (
                    <span className="req-short">only {cat.availableRooms} available</span>
                  )}
                  <input
                    type="number" min={0} className="form-input alloc-num"
                    value={rooms === 0 ? '' : rooms}
                    placeholder="0"
                    onChange={(e) => handleAllocationChange(cat, Math.max(0, Number(e.target.value) || 0))}
                  />
                </div>
              );
            })}
          </div>

          {/* Occupancy guidance — advisory, never a block. A hard stop pushes
              staff to under-report pax, destroying the number the rule protects.
              Capacity uses the MAX of the allocated rooms; "normal" is base. */}
          {(() => {
            const pax = commercialLines.reduce((a, l) => a + (l.numAdults || 0) + (l.numChildren || 0), 0);
            const capacity = detailLines.reduce((a, l) => {
              const cat = categories.find((c) => c.roomCategoryId === l.roomCategoryId);
              return a + (cat?.maxOccupancy ?? 0) * (l.numberOfRooms || 0);
            }, 0);
            const normal = detailLines.reduce((a, l) => {
              const cat = categories.find((c) => c.roomCategoryId === l.roomCategoryId);
              return a + (cat?.baseOccupancy ?? 0) * (l.numberOfRooms || 0);
            }, 0);
            if (capacity > 0 && pax > capacity) {
              return (
                <div className="wi-warn" style={{ marginTop: '0.5rem' }}>
                  ⚠ {pax} planned pax exceed the maximum allowable {capacity} for the allocated
                  rooms (normal occupancy {normal}). Allowed — review before confirming.
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Category cards — per-room only; per-pax renders the branch above. */}
      {rateBasis === 'PER_ROOM' && availabilityResult && categories.length > 0 ? (
        <div className={checkingAvailability ? 'room-cards-loading' : undefined}>
          {categories.map(cat => {
            // A category can now hold several occupancy lines (double sharing,
            // triple sharing…). With none, one empty card offers the Add action.
            const catLines = detailLines.filter(l => l.roomCategoryId === cat.roomCategoryId);
            const rendered = catLines.length > 0 ? catLines : [undefined];
            return (
              <React.Fragment key={cat.roomCategoryId}>
                {rendered.map((ln, i) => (
                  <RoomCategoryCard
                    key={ln?.lineKey ?? cat.roomCategoryId}
                    category={cat}
                    line={ln}
                    /* Second and later lines repeat the same category, so the
                       availability header would just be noise — and misleading,
                       since the count is per category, not per line. */
                    compact={i > 0}
                    occupancyIndex={i}
                    numNights={numNights}
                    onAdd={handleAddLine}
                    onUpdate={handleUpdateLine}
                    onRemove={handleRemoveLine}
                    hasError={!!fieldErrors[`line-${cat.roomCategoryId}`]}
                    errorMessage={fieldErrors[`line-${cat.roomCategoryId}`]}
                    childAges={childAges}
                    allMealPlans={allMealPlans}
                    pricingModel={pricingModel}
                    canOversell={canOversell}
                  allCategories={categories}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </div>
      ) : checkingAvailability ? (
        <div style={{ padding: '1rem 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          Checking availability...
        </div>
      ) : null}

      {/* Availability calendar — REFERENCE, so it sits after the work rather than
          between the requirement and the grid, where it pushed the entry surface
          below the fold. The per-category counts the operator needs while adding
          a line are already in the add-row and the overflow proposal; this is for
          date-shopping. */}
      {availabilityResult && categories.length > 0 && (
        <div className="avail-collapse">
          <button
            type="button"
            className="avail-collapse-btn"
            onClick={() => setAvailOpen(o => !o)}
          >
            {availOpen ? '▾' : '▸'} Availability overview
            <span className="avail-collapse-hint">
              {availOpen ? 'hide' : 'show the 7-day grid'}
            </span>
          </button>
          {availOpen && (
            <AvailabilityCalendarPanel
              propertyId={propertyId}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default RoomRateSection;
